<template>
  <div>
    <v-snackbar v-model="snackbar" bottom left :timeout="4000">{{ snackbarText }}</v-snackbar>

    <v-dialog v-model="dialog" max-width="600px">
      <template v-slot:activator="{ on, attrs }">
        <v-btn tile dark class="mt-5 primary-color" v-bind="attrs" v-on="on">
          <v-icon size="20" class="mr-1">mdi-plus</v-icon>Upload in batch
        </v-btn>
      </template>
      <v-card class="pb-8">
        <v-card-title>
          <v-icon
            size="20"
            style="position: absolute; right: 20px; top: 20px; cursor: pointer;"
            @click="dialog = false"
          >mdi-close</v-icon>
        </v-card-title>

        <v-card-text class="pb-5">
          <v-container>
            <v-row>
              <v-col lg="12" class="pl-0">
                <div style="text-align: center;" class="mb-10">
                  <v-avatar color="#b3d4fc44" size="70">
                    <v-icon size="45" style="color: #6490f1;">mdi-upload</v-icon>
                  </v-avatar>
                  <h2
                    class="pt-5"
                    style="font-weight: 400; color: #171717;"
                  >Upload {{type}}s in batch</h2>
                </div>

                <v-file-input
                  class="px-5"
                  v-model="csv"
                  accept=".csv"
                  label="Choose file (File Format: CSV)"
                ></v-file-input>
                <p class="px-7">
                  Don't have the template?
                  <JsonExcel
                    :data="[{}]"
                    :fields="headers"
                    type="csv"
                    :name="templateName"
                    style="display: inline;"
                  >
                    <a href="#">Download here</a>
                  </JsonExcel>
                </p>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-btn
            style="margin: 0 auto;"
            class="primary-color px-5"
            width="50%"
            height="45px"
            dark
            text
            @click="dialog = false; onUploadCsv();"
          >Upload {{type}}s</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row justify="center">
      <v-dialog v-model="errorDialog" width="600px">
        <v-card>
          <v-card-title>
            <span class="headline">Oops. Some issues were found.</span>
          </v-card-title>
          <v-card-text class="mt-4">
            <p v-for="(message, index) in feedbackMessages" :key="index">{{message}}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color text @click="errorDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import JsonExcel from "vue-json-excel";
import Papa from "papaparse";

export default {
  props: {
    type: String,
  },
  mounted: async function () {
    this.headers = this.initializeHeaders(this.getUploadType());
    this.templateName = this.initializeTemplateName(this.getUploadType());
    this.unitCodes = await this.getUnitsCode();
    this.qualificationCodes = await this.getQualificationCode();
    this.centres = await this.getCentres();
    this.learners = await this.getLearners();
  },
  components: {
    JsonExcel,
  },
  data: function () {
    return {
      csv: null,
      dialog: false,
      headers: null,
      templateName: "",
      errorDialog: false,
      feedbackMessages: [],
      malformedEntries: [],
      unitCodes: [],
      centres: [],
      learners: [],
      olderRows: [],
      snackbar: false,
      snackbarText: "",
    };
  },
  watch: {
    csv: function (newValue) {
      this.csv = newValue;
    },
  },
  methods: {
    getLearners: async function () {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_ROOT_API}/learners?require=all`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.$store.state.jwt}`,
            },
          }
        );

        const data = await response.json();

        return data.learners;
      } catch (err) {
        console.log(err);
      }
    },

    getCentres: async function () {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_ROOT_API}/centres`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.$store.state.jwt}`,
            },
          }
        );

        const data = await response.json();

        return data.centres;
      } catch (err) {
        console.log(err);
      }
    },

    getUnitsCode: async function () {
      try {
        const response = await fetch(`${process.env.VUE_APP_ROOT_API}/units`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.jwt}`,
          },
        });

        const data = await response.json();

        return data.units.map((unit) => unit.code);
      } catch (err) {
        console.log(err);
      }
    },

    getQualificationCode: async function () {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_ROOT_API}/qualifications`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.$store.state.jwt}`,
            },
          }
        );

        const data = await response.json();

        return data.qualifications.map((qualification) => qualification.code);
      } catch (err) {
        console.log(err);
      }
    },

    getUploadType: function () {
      return this.type.toLowerCase();
    },

    initializeHeaders: function (type) {
      if (type == "unit") {
        return this.initializeUnitHeaders();
      } else if (type == "qualification") {
        return this.initializeQualificationHeaders();
      } else if (type == "learner") {
        return this.initializeLearnerHeaders();
      } else if (type == "centre") {
        return this.initializeCentreHeaders();
      } else if (type == "certification") {
        return this.initializeCertificationHeaders();
      }
    },

    initializeUnitHeaders: function () {
      return {
        name: "name",
        code: "code",
        uan: "uan",
        description: "description",
        status: "status",
        credits: "credits",
        number: "number",
        note: "note",
        startDate: "startDate",
        reviewDate: "reviewDate",
        gradingLanguage: "gradingLanguage",
        noOfResits: "noOfResits",
        deliveryMode: "deliveryMode",
      };
    },

    initializeQualificationHeaders: function () {
      return {
        name: "name",
        code: "code",
        description: "description",
        qan: "qan",
        noOfMandatoryUnits: "noOfMandatoryUnits",
        mandatoryUnits: "mandatoryUnits",
        noOfOptionalUnits: "noOfOptionalUnits",
        optionalUnits: "optionalUnits",
        startDate: "startDate",
        reviewDate: "reviewDate",
        status: "status",
        noOfResits: "noOfResits",
        gradingLanguage: "gradingLanguage",
        deliveryMode: "deliveryMode",
        learningOutcomes: "learningOutcomes",
        certificationTemplate: "certificationTemplate",
      };
    },

    initializeLearnerHeaders: function () {
      return {
        title: "title",
        firstApprovedDate: "firstApprovedDate",
        renewalDate: "renewalDate",
        ukprn: "ukprn",
        status: "status",
        firstName: "firstName",
        middleName: "middleName",
        lastName: "lastName",
        uln: "uln",
        dateOfBirth: "dateOfBirth",
        ethnicity: "ethnicity",
        nationality: "nationality",
        phoneNo: "phoneNo",
        mobileNo: "mobileNo",
        email: "email",
        addressLine1: "addressLine1",
        addressLine2: "addressLine2",
        addressLine3: "addressLine3",
        city: "city",
        postCode: "postCode",
        country: "country",
        qualificationCode: "qualificationCode",
        centreCode: "centreCode",
      };
    },

    initializeCentreHeaders: function () {
      return {
        name: "name",
        city: "city",
        firstApprovedDate: "firstApprovedDate",
        renewalDate: "renewalDate",
        ukprn: "ukprn",
        status: "status",
        addressLine1: "addressLine1",
        addressLine2: "addressLine2",
        addressLine3: "addressLine3",
        postcode: "postcode",
        country: "country",
        mainContactFirstName: "mainContactFirstName",
        mainContactMiddleName: "mainContactMiddleName",
        mainContactLastName: "mainContactLastName",
        mainContactEmail: "mainContactEmail",
        mainContactTelephone: "mainContactTelephone",
        mainContactMobile: "mainContactMobile",
        adminContactEmail: "adminContactEmail",
        adminContactTelephone: "adminContactTelephone",
        adminContactMobile: "adminContactMobile",
        accountContactEmail: "accountContactEmail",
        accountContactTelephone: "accountContactTelephone",
        accountContactMobile: "accountContactMobile",
      };
    },

    initializeCertificationHeaders: function () {
      return {
        learner: "learner",
        units: "units",
        status: "approved",
      };
    },

    initializeTemplateName: function (type) {
      let template;

      if (type == "unit") {
        template = "unit_template.csv";
      } else if (type == "qualification") {
        template = "qualification_template.csv";
      } else if (type == "learner") {
        template = "learner_template.csv";
      } else if (type == "centre") {
        template = "template.csv";
      }

      return template;
    },

    onUploadCsv: async function () {
      if (this.getUploadType() == "learner") {
        this.showSnackbar({ message: "Step 1 / 3: Validating document ..." });
      }

      Papa.parse(this.csv, {
        worker: true,
        header: true,
        skipEmptyLines: true,
        chunk: async function (results) {
          const modifiedData = this.modifiyCsvResults(results.data);

          if (!this.isCsvValid(modifiedData)) {
            this.displayMalformedData(this.malformedEntries);
            return;
          }

          const removedDuplicatedDate = modifiedData.filter((data, index) => {
            return !this.olderRows.includes(index);
          });

          try {
            if (this.getUploadType() == "learner") {
              this.showSnackbar({
                message: "Step 2 / 3: Uploading document ...",
              });
            }

            const response = await fetch(this.getPostApi(this.type), {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.$store.state.jwt}`,
              },
              body: JSON.stringify({ bulkItems: removedDuplicatedDate }),
            });

            const data = await response.json();
            this.showSnackbar(data);
          } catch (err) {
            console.log(err);
          }
        }.bind(this),
      });
    },

    modifiyCsvResults: function (data) {
      const type = this.getUploadType();

      if (type == "unit") {
        return this.modifyUnitData(data);
      } else if (type == "qualification") {
        return this.modifyQualificationData(data);
      } else if (type == "learner") {
        return this.modifyLearnerData(data);
      } else if (type == "centre") {
        return this.modifyCentreData(data);
      } else if (type == "certification") {
        return this.modifyCertificationData(data);
      }
    },

    modifyUnitData: function (data) {
      for (let i = 0; i < data.length; i++) {
        data[i].type = "accredited";
        data[i].gradingCriterion = "status";
        data[i].tqt = data[i].credits * 10;
        data[i].minimumHour = data[i].tqt * 0.32;
        data[i].level = data[i].code.match(/\d+/)[0][0]; // get the first number in alphanumeric unit code

        if (data[i].level == 1) {
          data[i].status = "Inactive";
        } else {
          data[i].status = "Active";
        }
      }

      return data;
    },

    modifyQualificationData: function (data) {
      for (let i = 0; i < data.length; i++) {
        data[i].type = "accredited";
        data[i].gradingCriterion = "status";
        data[i].tqt = data[i].credits * 10;
        data[i].minimumHour = data[i].tqt * 0.32;
        data[i].level = data[i].code.match(/\d+/)[0][0]; // get the first number in alphanumeric unit code
        data[i].certificationTemplate = data[i].certificationTemplate || null;
        data[i].status = data[i].status || "Active";
        data[i].learningOutcomes = null; // remove this later

        data[i].mandatoryUnits =
          data[i].mandatoryUnits != ""
            ? data[i].mandatoryUnits.replace(/\s*,\s*/g, ",").split(",") // remove white space between comma separated string
            : [];

        data[i].optionalUnits =
          data[i].optionalUnits != ""
            ? data[i].optionalUnits.replace(/\s*,\s*/g, ",").split(",") // remove white space between comma separated string
            : [];
      }

      return data;
    },

    modifyLearnerData: function (data) {
      for (let i = 0; i < data.length; i++) {
        data[i].registeredDate = data[i].registeredDate.split(" ")[0]; // remove time in datetime
        data[i].certificationIssueDate = data[i].achievementDate.split(" ")[0]; // remove time in datetime

        if (data[i].optionalUnits) {
          data[i].optionalUnits =
            data[i].optionalUnits != ""
              ? data[i].optionalUnits.replace(/\s*,\s*/g, ",").split(",") // remove white space between comma separated string
              : [];
        }
      }

      return data;
    },

    modifyCentreData: function (data) {
      const centres = [];

      for (let i = 0; i < data.length; i++) {
        const centre = {
          name: data[i]["*ContactName"],
          city: data[i]["POCity"],
          addressLine1: data[i]["POAddressLine1"],
          postcode: data[i]["POPostalCode"],
          country: data[i]["POCountry"],
          mainContactFirstName: data[i]["FirstName"],
          mainContactLastName: data[i]["LastName"],
          mainContactEmail: data[i]["EmailAddress"],
          mainContactTelephone: data[i]["PhoneNumber"],
          mainContactMobile: data[i]["MobileNumber"],
        };

        centres.push(centre);
      }

      return centres;
    },

    modifyCertificationData: function (data) {
      const certifications = [];

      for (let i = 0; i < data.length; i++) {
        const optionalUnits = data[i]["optionalUnits"].split(",");

        const formattedOptionalUnits = optionalUnits.reduce((units, unit) => {
          // remove empty string element
          if (unit != "") {
            units.push(unit.trim());
          }
          return units;
        }, []);

        const certification = {
          learner: data[i]["learner"],
          centre: data[i]["centre"],
          units: data[i]["units"].split(","),
          status: data[i]["status"],
          optionalUnits: formattedOptionalUnits,
        };

        certifications.push(certification);
      }

      return certifications;
    },

    isCsvValid: function (data) {
      if (this.getUploadType() == "unit") {
        this.validateUnitCsv(data);
      } else if (this.getUploadType() == "learner") {
        this.validateLearnerCsv(data);
      } else if (this.getUploadType() == "qualification") {
        this.validateQualificationCsv(data);
      } else if (this.getUploadType() == "centre") {
        this.validateCentreCsv(data);
      }

      return this.malformedEntries.length <= 0 ? true : false;
    },

    getUnitMandatoryFields: function () {
      return {
        name: null,
        code: null,
        type: null,
        level: null,
        credits: null,
        tqt: null,
        gradingCriterion: null,
        minimumHour: null,
      };
    },

    getQualificationMandatoryFields: function () {
      return {
        name: null,
        code: null,
        level: null,
        noOfMandatoryUnits: null,
        mandatoryUnits: null,
        optionalUnits: null, // not mandatory but need to check whether the units exists
        credits: null,
        type: null,
        tqt: null,
        minimumHour: null,
        gradingCriterion: null,
      };
    },

    getLearnerMandatoryFields: function () {
      return {
        firstName: null,
        lastName: null,
        dateOfBirth: null,
        ethnicity: null,
        nationality: null,
        email: null,
        addressLine1: null,
        city: null,
        postCode: null,
        country: null,
        qualificationCode: null,
        centreCode: null,
      };
    },

    getCentreMandatoryFields: function () {
      return {
        name: null,
        city: null,
        addresLine1: null,
        postCode: null,
        country: null,
        mainContactFirstName: null,
        mainContactLastName: null,
        mainContactEmail: null,
        mainContactTelephone: null,
        mainContactMobile: null,
      };
    },

    validateUnitCsv: function (data) {
      const mandatoryFields = this.getUnitMandatoryFields();

      for (let i = 0; i < data.length; i++) {
        const missingKeys = [];
        Object.keys(mandatoryFields).forEach((key) => {
          if (data[i][key] == "") {
            missingKeys.push(key);
          }
        });

        if (missingKeys.length > 0) {
          this.malformedEntries.push(
            `Missing column on row ${i + 2}: ${missingKeys.join(", ")}`
          );
        }
      }
    },

    validateQualificationCsv: function (data) {
      const mandatoryFields = this.getQualificationMandatoryFields();

      for (let i = 0; i < data.length; i++) {
        const missingKeys = [];
        Object.keys(mandatoryFields).forEach((key) => {
          if (data[i][key] == "" && key != "optionalUnits") {
            missingKeys.push(key);
          }
          if (key == "mandatoryUnits" || key == "optionalUnits") {
            for (let j = 0; j < data[i][key].length; j++) {
              if (!this.unitCodes.includes(data[i][key][j])) {
                this.malformedEntries.push(
                  `Row ${i + 2}: Unit code ${data[i][key][j]} does not exists.`
                );
              }
            }
          }
        });

        if (missingKeys.length > 0) {
          this.malformedEntries.push(
            `Row ${i + 2}: Missing ${missingKeys.join(", ")}.`
          );
        }
      }
    },

    validateLearnerCsv: function (data) {
      this.checkIfLearnerExisted(data); // check with database
      this.checkIfLearnerDuplicate(data); // check within csv file

      const mandatoryFields = this.getLearnerMandatoryFields();
      const missing = [];

      for (let i = 0; i < data.length; i++) {
        const missingKeys = [];
        Object.keys(mandatoryFields).forEach((key) => {
          if (data[i][key] == "") {
            missingKeys.push(key);
          }
          if (key == "qualificationCode") {
            if (!this.qualificationCodes.includes(data[i][key])) {
              if (!missing.includes(data[i][key])) {
                missing.push(data[i][key]);
              }
              this.malformedEntries.push(
                `Row ${i + 2}: Qualification code ${
                  data[i][key]
                } does not exists.`
              );
            }
          }
        });

        if (missingKeys.length > 0) {
          this.malformedEntries.push(
            `Missing column on row ${i + 2}: ${missingKeys.join(", ")}`
          );
        }
      }
    },

    checkIfLearnerExisted: function (data) {
      for (let i = 0; i < data.length; i++) {
        if (this.malformedEntries.length > 10) {
          break;
        }
        for (let j = 0; j < this.learners.length; j++) {
          if (
            data[i].firstName == this.learners[j].firstName &&
            data[i].lastName == this.learners[j].lastName &&
            data[i].email == this.learners[j].email &&
            data[i].qualificationCode == this.learners[j].qualification
          ) {
            this.malformedEntries.push(
              `Row ${i + 2}: Learner ${data[i].firstName} ${
                data[i].middleName
              } ${data[i].lastName} already existed in database.`
            );
            break;
          }
        }
      }
    },

    checkIfLearnerDuplicate: function (data) {
      for (let i = 0; i < data.length; i++) {
        for (let j = i + 1; j < data.length; j++) {
          if (
            data[i].firstName == data[j].firstName &&
            data[i].lastName == data[j].lastName &&
            data[i].email == data[j].email &&
            data[i].qualificationCode == data[j].qualificationCode &&
            data[i].centreCode == data[j].centreCode
          ) {
            const olderRow = this.compareDate(
              { date: data[i].registeredDate, index: i + 1 },
              { date: data[j].registeredDate, index: j + 1 }
            );

            this.olderRows.push(olderRow);
            break;
          }
        }
      }
    },

    compareDate: function (data1, data2) {
      const aDateParts = data1.date.split("/");
      const bDateParts = data2.date.split("/");

      const aDate = new Date(+aDateParts[2], aDateParts[1] - 1, +aDateParts[0]);
      const bDate = new Date(+bDateParts[2], bDateParts[1] - 1, +bDateParts[0]);

      if (aDate - bDate >= 0) {
        return data2.index;
      } else if (aDate - bDate < 0) {
        return data1.index;
      }
    },

    validateCentreCsv: function (data) {
      this.checkIfCentreExisted(data);
      this.checkIfCentreDuplicate(data);

      const mandatoryFields = this.getCentreMandatoryFields();

      for (let i = 0; i < data.length; i++) {
        const missingKeys = [];
        Object.keys(mandatoryFields).forEach((key) => {
          if (data[i][key] == "") {
            missingKeys.push(key);
          }
        });

        if (missingKeys.length > 0) {
          this.malformedEntries.push(
            `Missing column on row ${i + 2}: ${missingKeys.join(", ")}`
          );
        }
      }
    },

    checkIfCentreExisted: function (data) {
      for (let i = 0; i < data.length; i++) {
        for (let j = 0; j < this.centres.length; j++) {
          if (
            data[i].name == this.centres[j].name &&
            data[i].postcode == this.centres[j].postcode
          ) {
            this.malformedEntries.push(
              `Centre on row ${i + 2}: ${data[i].name} already existed.`
            );
            break;
          }
        }
      }
    },

    checkIfCentreDuplicate: function (data) {
      const duplicateCentre = [];

      for (let i = 0; i < data.length; i++) {
        loop1: for (let j = 0; j < data.length; j++) {
          if (i == j) {
            continue;
          }
          if (
            data[i].name == data[j].name &&
            data[i].postcode == data[j].postcode
          ) {
            for (let k = 0; k < duplicateCentre.length; k++) {
              if (
                data[i].name == duplicateCentre[k].name &&
                data[i].postcode == duplicateCentre[k].postcode
              ) {
                break loop1;
              }
            }
            duplicateCentre.push(data[i]);
            this.malformedEntries.push(
              `Centre duplicates on row ${i + 2} and ${j + 2}.`
            );
            break;
          }
        }
      }
    },

    displayMalformedData: function (data) {
      this.feedbackMessages = data;
      this.errorDialog = true;
    },

    getPostApi: function (type) {
      if (type == "unit") {
        return `${process.env.VUE_APP_ROOT_API}/units/bulk`;
      } else if (type == "learner") {
        return `${process.env.VUE_APP_ROOT_API}/learners/bulk`;
      } else if (type == "qualification") {
        return `${process.env.VUE_APP_ROOT_API}/qualifications/bulk`;
      } else if (type == "centre") {
        return `${process.env.VUE_APP_ROOT_API}/centres/bulk`;
      } else if (type == "certification") {
        return `${process.env.VUE_APP_ROOT_API}/certifications/bulk`;
      }
    },

    showSnackbar: function (data) {
      this.snackbarText = data.message;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
</style>